import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Stack, Group, Text } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import dayjs from "dayjs";

import { useDisplaySchedule, useEvents } from "@/hooks";
import { useVenuesContext } from "@/contexts";
import { Event, Display } from "@/utils";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppIcon,
  AppToolbar,
  EmptyState,
  DateRangeSelector,
  DateRangeValue,
} from "@/components/generic";
import {
  ScheduleEventsTable,
  VenueSelector,
  EventFormModal,
  NeedAddVenue,
} from "@/components/features";

export interface ScheduleEvent extends Event {
  isPast?: boolean;
}

export const SchedulePage: React.FC<PageProps> = (props: PageProps) => {
  const { selectedVenue, setSelectedVenue, hasVenues } = useVenuesContext();

  // Use the display schedule hook
  const {
    scheduleData,
    loading,
    isEmpty,
    lastError,
    clearError,
    loadSchedule,
  } = useDisplaySchedule({
    autoLoad: false,
  });

  // Single events hook - updated to use new API
  const {
    isUpdating,
    selectedEvent,
    create,
    update,
    setSelectedEvent,
    lastError: eventsLastError,
    clearError: clearEventsError,
  } = useEvents({
    pageSize: 20,
    autoLoad: false,
  });

  // Display selection state
  const [selectedDisplay, setSelectedDisplay] = useState<Display | null>(null);

  // Date range state using the new component
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    viewMode: "day",
    displayDate: "",
  });

  const [eventFormModalOpened, setEventFormModalOpened] =
    useState<boolean>(false);

  // Update filter when date range, venue, or display changes
  useEffect(() => {
    if (selectedVenue?.id && dateRange.startDate && dateRange.endDate) {
      const filter = {
        venueId: selectedVenue.id,
        displayId: selectedDisplay?.id,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        date: dateRange.viewMode == "day" ? dateRange.startDate : undefined,
        preview: true,
      };

      console.log("Loading data with filter:", filter, dateRange);
      loadSchedule(filter);
    }
  }, [
    selectedVenue?.id,
    selectedDisplay?.id,
    dateRange.startDate,
    dateRange.endDate,
    dateRange.displayDate,
    dateRange.viewMode,
    loadSchedule,
  ]);

  // Extract events from schedule data
  const events = useMemo(() => {
    const loadedEvents = scheduleData.flatMap((data) => data.events || []);
    return loadedEvents;
  }, [scheduleData]);

  // Sort events and mark past events
  const processedEvents: ScheduleEvent[] = useMemo(() => {
    const now = dayjs();

    return events
      .map((event) => ({
        ...event,
        isPast: dayjs(`${event.eventDate} ${event.endTime}`).isBefore(now),
      }))
      .sort((a, b) => {
        const dateA = dayjs(`${a.eventDate} ${a.startTime}`);
        const dateB = dayjs(`${b.eventDate} ${b.startTime}`);
        return dateA.diff(dateB);
      });
  }, [events]);

  // Handle venue selection
  const handleVenueChange = useCallback(
    (_: string | null, venue: any) => {
      setSelectedVenue(venue);
      // Reset display selection when venue changes
      setSelectedDisplay(null);
      clearError();
    },
    [setSelectedVenue, clearError]
  );

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (newDateRange: DateRangeValue) => {
      setDateRange(newDateRange);
      clearError();
    },
    [clearError]
  );

  const handleRefresh = useCallback(() => {
    const filter = {
      venueId: selectedVenue?.id,
      displayId: selectedDisplay?.id,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      date: dateRange.viewMode == "day" ? dateRange.startDate : undefined,
      preview: true,
    };
    loadSchedule(filter);
  }, [selectedVenue, selectedDisplay, dateRange, loadSchedule]);

  // Handle edit single event
  const handleEditEvent = useCallback(
    (event: Event) => {
      setSelectedEvent(event);
      setEventFormModalOpened(true);
    },
    [setSelectedEvent]
  );

  const handleAddEvent = useCallback(() => {
    setSelectedEvent({
      id: "",
      eventDate: dateRange.startDate,
    });
    setEventFormModalOpened(true);
  }, [setSelectedEvent, dateRange]);

  const handleEventFormModalClose = useCallback(
    (event?: Event) => {
      setEventFormModalOpened(false);
      setSelectedEvent(null);
      clearEventsError();
      if (event) {
        handleRefresh();
      }
    },
    [handleRefresh, setSelectedEvent, clearEventsError]
  );

  // Handle view event (placeholder)
  const handleViewEvent = useCallback((event: ScheduleEvent) => {
    // TODO: Implement view event functionality
    console.log("View event:", event);
  }, []);

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Venue selector and view controls */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "venue-selector",
              items: [
                <VenueSelector
                  key="venue-selector"
                  value={selectedVenue?.id || null}
                  onChange={handleVenueChange}
                  placeholder="Select venue"
                  style={{ minWidth: 250 }}
                  disabled={loading}
                  required
                  clearable={false}
                />,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "date-range-controls",
              items: [
                <DateRangeSelector
                  key="date-range-selector"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  disabled={loading}
                />,
              ],
              align: "right",
              gap: "sm",
            },
          ]}
        />

        {/* Error display */}
        {lastError && (
          <Group gap="xs" c="red">
            <AppIcon iconName="AlertCircle" size={16} />
            <Text size="sm">{lastError}</Text>
            <AppButton variant="secondary" size="xs" onClick={clearError}>
              Dismiss
            </AppButton>
          </Group>
        )}

        {/* Events table */}
        {selectedVenue?.id && (
          <ScheduleEventsTable
            events={processedEvents}
            loading={loading}
            loadingMore={false}
            hasMore={false}
            sortBy={[]}
            onSortChange={() => {}}
            onLoadMore={() => {}}
            onEditEvent={handleEditEvent}
            onViewEvent={handleViewEvent}
            size="md"
            hideOnNoRecords={true}
            //disabled={loading}
          />
        )}

        {/* Empty state for no venue exist */}
        <NeedAddVenue
          display={!hasVenues && !loading}
          descriptionExtra="Then add rooms in it and then you can start adding events"
        />

        {/* Empty state for no venue selected */}
        <EmptyState
          display={!selectedVenue?.id && hasVenues}
          title="Select a venue"
          description="Choose a venue from the dropdown to view its schedule"
        />

        {/* Empty state for no events */}
        <EmptyState
          display={!!selectedVenue?.id && isEmpty && !loading}
          title="No events scheduled"
          description={`No events found for ${dateRange.displayDate.toLowerCase()}`}
          actionIcon="Plus"
          actionLabel="Add Event"
          onAction={handleAddEvent}
        />

        {/* Single event form modal */}
        <EventFormModal
          opened={eventFormModalOpened}
          onClose={handleEventFormModalClose}
          event={selectedEvent}
          venueId={selectedVenue?.id || ""}
          onCreateEvent={create}
          onUpdateEvent={update}
          isUpdating={isUpdating}
          lastError={eventsLastError}
          onClearError={clearEventsError}
        />
      </Stack>
    </DashboardLayout>
  );
};
